"use client";

import { forwardRef, type InputHTMLAttributes } from "react";

export type ChoiceType = "checkbox" | "radio";

export type ChoiceProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  choiceType: ChoiceType;
};

const CONTROL_BASE_CLASSES =
  "pointer-events-none absolute inset-0 border border-[var(--fz-choice-border)] bg-[var(--fz-choice-bg)] transition-colors peer-checked:border-[var(--fz-choice-checked-bg)] peer-checked:bg-[var(--fz-choice-checked-bg)] peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--fz-choice-focus-ring)] peer-focus-visible:ring-offset-1 peer-disabled:opacity-[var(--fz-choice-disabled-opacity)] peer-aria-invalid:border-[var(--fz-choice-error)]";

export const Choice = forwardRef<HTMLInputElement, ChoiceProps>(function Choice(
  { choiceType, className = "", ...props },
  ref,
) {
  const circular = choiceType === "radio";

  return (
    <span className={`relative inline-flex h-4 w-4 shrink-0 ${className}`}>
      <input
        {...props}
        ref={ref}
        type={choiceType}
        data-fz-choice={choiceType}
        className={`${className} peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed`}
      />
      <span className={`${CONTROL_BASE_CLASSES} ${circular ? "rounded-full" : "rounded-[0.25rem]"}`} />
      {circular ? (
        <span className="pointer-events-none absolute inset-[4px] rounded-full bg-[var(--fz-choice-indicator)] opacity-0 peer-checked:opacity-100 peer-disabled:opacity-[var(--fz-choice-disabled-opacity)]" />
      ) : (
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          className="pointer-events-none absolute inset-0 z-[1] h-full w-full p-[3px] text-[var(--fz-choice-indicator)] opacity-0 peer-checked:opacity-100 peer-disabled:opacity-[var(--fz-choice-disabled-opacity)]"
        >
          <path d="m3.25 8.25 3 3 6.5-6.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
});
